import { readQueryParams } from '@cp/src/utils/QueryParamsHelper';
import { useNavigate } from 'react-router-dom';
import { getTasksForMeColumnDefs } from './columnDefs';
import { ClinCellEditor } from '../../sensei/components/DailyPlanning/CommitmentTracker/BasicCellEditor';
import { BasicSelectEditor } from '../../sensei/components/DailyPlanning/CommitmentTracker/BasicSelectEditor';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RenderAgTable from '../../ExternalDashboardSensei/components/RenderAgTable';
import {
  acknowledgeTicket,
  fetchSingleTicket
} from '../../CsaTicketing/actions/CsaTicketingActions';
import { useDispatch, useSelector } from 'react-redux';
import SingleTicketDrawer from '../../CsaTicketing/components/SingleTicketDrawer';
import { RootState } from '@cp/src/store';
import TableHeader from '../TableHeader';
import {
  fetchDynamicFilters,
  fetchTicketsForTable,
  updateTicketStatus
} from '../actions/CsaCoordintationActions';
import Loader from '@cp/src/components/Loader/Loader';
import styles from '../index.module.scss';
import CloseTicketModal from '../CloseTicketModal';
import { ITableTicket, TableName } from '../types';
import { getUpdatedColDefs, setInitialParams, updateAcknowlegementStatus } from '../utils';
import { CSA_POLLING_TIME, TASKS_FOR_ME_TABLE } from '../contants';
import { TicketStatus } from '../../CsaTicketing/types';
import { poll } from '@cp/src/utils/polling';
import { noop } from '@navi/web-ui/lib/utils/common';
import { CellValueChangedEvent } from 'ag-grid-community';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';

const TasksForMe = () => {
  const params = readQueryParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const unsubscribe = useRef<() => void>();

  const tasksForMeTickets = useSelector((state: RootState) => state?.csaCoordination?.tasksForMe);
  const tableDataIsLoading = useSelector((state: RootState) => state?.csaCoordination?.isLoading);
  const tasksForMeFilterScehma = useSelector(
    (state: RootState) => state?.csaCoordination?.tasksForMeFilterScehma
  );
  const singleTicket = useSelector(
    (state: RootState) => state?.csaTicketing?.ticket?.ticketDetails
  );
  const singleTicketLoading = useSelector(
    (state: RootState) => state?.csaTicketing?.ticket?.isLoading
  );
  const userId = useSelector((state: RootState) => state?.common?.userData?.referenceId);

  const [showSingleTicketDrawer, setShowSingleTicketDrawer] = useState(false);
  const [showCloseTicketModal, setShowCloseTicketModal] = useState(false);

  const { data = [], pages } = tasksForMeTickets || {};

  const totalElements = pages?.totalElements || 0;
  const currentPage = Number(params?.tasksForMe?.pageNumber) || 1;
  const pageSize = Number(params?.tasksForMe?.pageSize) || 10;

  useEffect(() => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_MY_TASKS_LOAD_SUCCESSFUL);
    dispatch(fetchDynamicFilters());
    setInitialParams(params, navigate, TableName.TASKS_FOR_ME);
  }, []);

  useEffect(() => {
    unsubscribe.current = poll(
      () => fetchTasksForMeData(),
      json => json,
      CSA_POLLING_TIME,
      noop
    );

    return () => {
      unsubscribe.current?.();
    };
  }, []);

  const toggleCloseTicketModal = () => {
    setShowCloseTicketModal(!showCloseTicketModal);
  };
  const toggleSingleTicketDrawer = () => {
    setShowSingleTicketDrawer(!showSingleTicketDrawer);
  };

  const fetchTasksForMeData = () => {
    dispatch(fetchTicketsForTable(userId));
  };

  const viewDetailsHandler = useCallback(
    (ticket: ITableTicket) => {
      const { isAcknowledgedByAssignee, newUpdatesCount, referenceId } = ticket || {};
      if (!isAcknowledgedByAssignee || newUpdatesCount) {
        acknowledgeTicket(referenceId, () => {
          dispatch(updateAcknowlegementStatus(tasksForMeTickets, ticket));
        });
      }
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_TASK_VIEW_DETAIL_CLICKED, {
        ticketId: referenceId
      });
      dispatch(fetchSingleTicket(referenceId));
      setShowSingleTicketDrawer(true);
    },
    [data]
  );

  const statusRef = useRef<string>('');

  const ticketStatusHandler = (
    value: string,
    setValue: (val: string) => void,
    stopEditing: any,
    referenceId: string
  ) => {
    if (value === TicketStatus.CLOSED) {
      statusRef.current = value;
      stopEditing();
      dispatch(fetchSingleTicket(referenceId));
      setShowCloseTicketModal(true);
      return;
    }
    setValue(value);
  };

  const onEdit = (cellChangeData: CellValueChangedEvent) => {
    const changedColumn = cellChangeData.column.getColId() as any;
    if (!changedColumn) return;
    const { newValue, oldValue } = cellChangeData;

    if (newValue !== TicketStatus.CLOSED) {
      dispatch(
        updateTicketStatus({
          payload: {
            status: newValue,
            ticketReferenceId: cellChangeData?.data?.referenceId
          },
          errorCallbackFn: () => {
            cellChangeData?.node?.updateData({
              ...cellChangeData?.data,
              status: oldValue
            });
          }
        })
      );
    }
  };

  const colDefs = useMemo(() => {
    const colData = getTasksForMeColumnDefs(viewDetailsHandler, ticketStatusHandler);
    const newColumnData = getUpdatedColDefs(colData, params, TableName.TASKS_FOR_ME);
    return newColumnData;
  }, [params?.tasksForMe?.sortByField, viewDetailsHandler]);

  const handleCellClick = (cell: any) => {
    if (cell.colDef.field === 'status') {
      return;
    }
    viewDetailsHandler(cell.data);
  };

  return (
    <div>
      <TableHeader
        filterSchema={tasksForMeFilterScehma}
        fetchData={fetchTasksForMeData}
        tableName={TableName.TASKS_FOR_ME}
        clickstreamTableName={TASKS_FOR_ME_TABLE}
      />
      <RenderAgTable
        key={params?.tasksForMe?.sortByField}
        data={JSON.parse(JSON.stringify(data))}
        colDefs={colDefs}
        tableName={TableName.TASKS_FOR_ME}
        tableHeight={window?.innerHeight - 280} // 280 is header height
        sizeColumnsToFit
        isServerSortable={true}
        callBackFn={fetchTasksForMeData}
        pageDetails={{ totalElements, currentPage, pageSize }}
        headerHeight={60}
        customTableClass="csaTable"
        rowHeight={64}
        frameworkComponents={{
          clinCellEditor: ClinCellEditor,
          basicSelectEditor: BasicSelectEditor
        }}
        stopEditingWhenCellsLoseFocus={true}
        onCellClicked={handleCellClick}
        onCellValueChanged={onEdit}
        rowClass={'cursor-pointer'}
      />
      {showCloseTicketModal && (
        <CloseTicketModal
          toggleCloseTicketModal={toggleCloseTicketModal}
          status={statusRef.current}
        />
      )}
      <SingleTicketDrawer
        isOpen={showSingleTicketDrawer}
        toggleDrawer={toggleSingleTicketDrawer}
        ticket={singleTicket}
        isLoading={singleTicketLoading}
      />
      <Loader show={tableDataIsLoading} className={styles.loadingState} animate={false} />
    </div>
  );
};

export default TasksForMe;
